import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaModeloStruct } from '@shared/services/structs/formula/formula-modelo.struct';
import { ExpressaoToken } from './expressao-token';
import { VigenciaInfo } from './vigencia-info';

export { ExpressaoToken } from './expressao-token';
export { VigenciaInfo } from './vigencia-info';

/** O significado do modelo é um dado do catálogo, independente da variável de saída. */
export function rotuloModeloFormula(
    formula: FormulaCatalogoItemStruct,
    modelo: FormulaModeloStruct,
): string {
    const identificadorModelo = modelo.modelo.trim().toUpperCase();
    return modelo.nome ? `Modelo ${identificadorModelo} - ${modelo.nome}` : `Modelo ${identificadorModelo}`;
}

export function parseDataFormula(valor: string | null | undefined): Date | null {
    if (!valor) return null;

    // Datas no formato "YYYY-MM-DD" são interpretadas por new Date() como UTC meia-noite;
    // em fusos negativos (ex.: Brasil UTC-3) isso recua para o dia anterior. Tratamos como
    // data LOCAL para que a comparação de vigência reflita o dia informado pelo usuário.
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
    if (iso) {
        const data = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
        data.setHours(0, 0, 0, 0);
        return isNaN(data.getTime()) ? null : data;
    }

    const data = new Date(valor);
    if (isNaN(data.getTime())) return null;
    data.setHours(0, 0, 0, 0);
    return data;
}

function formatarData(data: Date): string {
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
}

/**
 * Descreve a situação de vigência de um modelo de fórmula para exibição no
 * header do card (texto, ícone Material e classe de cor — ver styles.css .vigencia-header).
 */
export function descreverVigencia(modelo: FormulaModeloStruct | null | undefined): VigenciaInfo {
    const inicio = parseDataFormula(modelo?.dataVigenciaInicio);
    const fim = parseDataFormula(modelo?.dataVigenciaFim);

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    // Vigência ainda não iniciada (futura)
    if (inicio && inicio.getTime() > hoje.getTime()) {
        return {
            texto: `Vigência a partir de ${formatarData(inicio)}`,
            icone: 'event_upcoming',
            classe: 'vigencia-header--futura',
        };
    }

    // Vigência expirada
    if (fim && fim.getTime() < hoje.getTime()) {
        return {
            texto: `Vigência expirada em ${formatarData(fim)}`,
            icone: 'event_busy',
            classe: 'vigencia-header--expirada',
        };
    }

    // Vigência em curso com data final definida
    if (fim) {
        return {
            texto: `Vigência até ${formatarData(fim)}`,
            icone: 'event_available',
            classe: 'vigencia-header--vigente',
        };
    }

    // Vigência permanente (sem data final), eventualmente iniciada no passado
    return {
        texto: 'Vigência permanente',
        icone: 'all_inclusive',
        classe: 'vigencia-header--permanente',
    };
}

/** Retorna o modelo atualmente ativo para a fórmula (personalizado da empresa ou padrão do sistema). */
export function modeloAtivo(formula: FormulaCatalogoItemStruct): FormulaModeloStruct | null {
    if (!formula.modelos || formula.modelos.length === 0) return null;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const vigentes = formula.modelos.filter(modelo => {
        const inicio = parseDataFormula(modelo.dataVigenciaInicio);
        const fim = parseDataFormula(modelo.dataVigenciaFim);
        return (!inicio || inicio <= hoje) && (!fim || fim >= hoje);
    });
    if (formula.chaveFormulaModeloEmpresa) {
        const emp = vigentes.find(m => m.chaveFormulaModelo === formula.chaveFormulaModeloEmpresa);
        if (emp) return emp;
    }
    return vigentes.find(m => m.isModeloPadraoSistema) || null;
}

/** Quebra a expressão em tokens, identificando variáveis pelo delimitador canônico { }. */
export function tokenizarExpressao(expressao: string): ExpressaoToken[] {
    if (!expressao?.trim()) return [];
    const partes = expressao.match(/\{[^}]+\}|#[^#]+#|@[A-Za-z]+|>=|<=|<>|[+\-*/()><=,]|[A-Za-z]+/g) ?? [];
    return partes.map(p => {
        const isVar = p.startsWith('{') && p.endsWith('}');
        return { texto: isVar ? p.slice(1, -1) : p, isVariavel: isVar };
    });
}
